import './src/styles/global.scss';
import posthog from 'posthog-js';
import React from 'react';
import RootLayout from './src/layouts/rootLayout';

const CHUNK_LOAD_ERROR_PATTERN = /(ChunkLoadError|Loading (CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module)/i;

// Store the original referrer globally but scoped to this module
let originalReferrer = null;
let originalReferringDomain = null;

const getErrorText = error => {
    if (!error) return '';
    if (typeof error === 'string') return error;

    return [
        error.name,
        error.message,
        error.stack
    ].filter(Boolean).join(' ');
};

const isChunkLoadError = event => {
    return CHUNK_LOAD_ERROR_PATTERN.test([
        event?.message,
        getErrorText(event?.error),
        getErrorText(event?.reason)
    ].filter(Boolean).join(' '));
};

const recoverFromChunkLoadError = event => {
    if (!isChunkLoadError(event)) return;

    const reloadKey = `aida:chunk-load-reload:${window.location.pathname}${window.location.search}`;
    try {
        if (window.sessionStorage.getItem(reloadKey)) return;
        window.sessionStorage.setItem(reloadKey, '1');
    }
    catch (error) {
        return;
    }

    event.preventDefault();
    window.location.reload();
};

export const onClientEntry = () => {
    // 1. Capture the original referrer immediately on client entry
    // This is before React hydrates and LanguageDetector's useEffect runs.
    if (typeof window !== 'undefined') {
        window.addEventListener('error', recoverFromChunkLoadError, true);
        window.addEventListener('unhandledrejection', recoverFromChunkLoadError, true);

        originalReferrer = document.referrer;
        try {
            if (originalReferrer) {
                originalReferringDomain = new URL(originalReferrer).hostname;
            }
        }
        catch (e) {
            // Invalid URL in referrer, treat as no domain
            originalReferringDomain = null;
            console.warn('Could not parse original referrer URL:', originalReferrer, e);
        }
        console.log('onClientEntry: Original referrer captured:', originalReferrer);
    }

    posthog.init('phc_uTMjoFMvhvbjhO7bw2a6aARfehIxhvXh7y9DcZy8UPv', {
        api_host: 'https://eu.posthog.com',
        persistence: 'localStorage',
        // Use the 'loaded' callback to ensure PostHog is ready
        loaded: function (posthogInstance) {
            console.log('PostHog loaded. Original referrer:', originalReferrer, 'Original domain:', originalReferringDomain);
            // 2. Register the original referrer with PostHog if it's external
            // We only want to override if originalReferrer exists AND it's not from our own domain.
            // Otherwise, PostHog's default handling for direct traffic or internal referrers (after your redirect) is fine.
            if (originalReferrer && originalReferringDomain && !window.location.hostname.includes(originalReferringDomain)) {
                const propertiesToRegister = {
                    '$initial_referrer_url': originalReferrer,
                    '$initial_referring_domain': originalReferringDomain
                };
                // You can also add your own custom properties if you want to be explicit
                propertiesToRegister[ 'custom_true_initial_referrer' ] = originalReferrer;
                propertiesToRegister[ 'custom_true_initial_referring_domain' ] = originalReferringDomain;

                posthogInstance.register_once(propertiesToRegister);
                console.log('PostHog register_once called with:', propertiesToRegister);
            }
            else if (!originalReferrer) {
                console.log('No original external referrer to register (direct traffic or empty).');
                // For direct traffic, PostHog will correctly set $initial_referrer_url to empty or current URL based on its logic.
                // We don't want to force it if originalReferrer is empty.
            }
            else {
                console.log('Original referrer is internal, letting PostHog handle normally.');
            }
        }
    });
    window.posthog = window.posthog || posthog;
    console.log('onClientEntry: PostHog initialized.');
};

export const onInitialClientRender = () => {

};

export const onRouteUpdate = ({ location, prevLocation }) => {
    // Ensure PostHog is loaded before capturing pageview
    if (window.posthog) {
        window.posthog.capture('$pageview');
        console.log('onRouteUpdate: $pageview captured for:', location.pathname, 'Previous location:', prevLocation ? prevLocation.pathname : 'N/A');
    }
    else {
        console.warn('onRouteUpdate: PostHog not available yet.');
    }
};

export const wrapRootElement = ({ element }) => <RootLayout>{element}</RootLayout>;
